import React, { useState, useCallback, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Modal,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Dropdown,
  MenuProps,
  Form,
  Input,
  Select,
  Alert,
  DatePicker
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TableRowSelection } from 'antd/es/table/interface';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  StopOutlined,
  MoreOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigationHelper } from '../../../hooks/useNavigationHelper';
// PageHeader import removed

import type {
  PlanogramTemplate,
  TemplateFilterParams,
  PaginationParams,
  TemplateStatus
} from '../../../types/plan-template';
import { PlanogramTemplateService } from '../../../services/planogramTemplateService';
import {
  PLAN_TYPES,
  TEMPLATE_STATUSES,
  UI_CONFIG,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES
} from '../../../constants';
import FilterPanel from '../../../components/common/FilterPanel';
import BatchActionToolbar from '../../../components/common/BatchActionToolbar';
import TemplateWizard from "../components/TemplateWizard";


const { Title } = Typography;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

interface PlanogramTemplateListProps {
  onTemplateSelect?: (template: PlanogramTemplate) => void;
  selectionMode?: boolean;
}

const PlanogramTemplateList: React.FC<PlanogramTemplateListProps> = ({
  onTemplateSelect,
  selectionMode = false,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigation = useNavigationHelper();

  const [wizardState, setWizardState] = useState<{
    visible: boolean;
    mode: 'create' | 'edit';
    templateId?: string;
  }>({
    visible: false,
    mode: 'create'
  });

  const handleOpenWizard = (mode: 'create' | 'edit', templateId?: string) => {
    setWizardState({
      visible: true,
      mode,
      templateId
    });
    console.log("wizardState:", wizardState)

  };

  const handleCloseWizard = () => {
    setWizardState(prev => ({
      ...prev,
      visible: false
    }));
  };

  const handleWizardComplete = async (template: PlanogramTemplate) => {
    console.log('Template saved:', template);
    handleCloseWizard();
    // 刷新表格数据
    await loadTemplates();
    // 清空选中状态
    setSelectedRowKeys([]);
  };
  
  // State management
  const [templates, setTemplates] = useState<PlanogramTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagination, setPagination] = useState<PaginationParams>({
    current: 1,
    pageSize: UI_CONFIG.DEFAULT_PAGE_SIZE
  });
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<TemplateFilterParams>({});
  const [statistics, setStatistics] = useState({
    total: 0,
    published: 0,
    draft: 0,
    disabled: 0
  });
  
  // Load templates data
  const loadTemplates = useCallback(async (
    currentFilters: TemplateFilterParams = filters,
    currentPagination: PaginationParams = pagination
  ) => {
    setLoading(true);
    try {
      const response = await PlanogramTemplateService.getTemplateList(
        currentFilters,
        currentPagination
      );

      if (response.success) {
        const { tableData, paging } = response.data;
        setTemplates(tableData);
        setTotal(paging.totalCount);
        
        // Update statistics
        const stats = {
          total: paging.totalCount,
          published: tableData.filter(t => t.status === 'Published').length,
          draft: tableData.filter(t => t.status === 'Draft').length,
          disabled: tableData.filter(t => t.status === 'Disabled').length
        };
        setStatistics(stats);
      } else {
        message.error(response.message || ERROR_MESSAGES.OPERATION_FAILED);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      message.error(ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination]);

  // Initial load
  useEffect(() => {
    loadTemplates();
  }, []);

  // Handle pagination change
  const handleTableChange = (page: number, pageSize: number) => {
    const newPagination = { current: page, pageSize };
    setPagination(newPagination);
    loadTemplates(filters, newPagination);
  };

  // Handle row selection
  const rowSelection: TableRowSelection<PlanogramTemplate> = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: PlanogramTemplate) => ({
      disabled: selectionMode && record.status === 'Disabled',
    }),
  };

  // Handle template actions
  const handleView = (template: PlanogramTemplate) => {
    if (onTemplateSelect) {
      onTemplateSelect(template);
    } else {
      navigation.goToTemplateDetail(template.id);
    }
  };

  const handleEdit = (template: PlanogramTemplate) => {
    handleOpenWizard?.('edit', template.id);
  };
  
  // 批量启用模板
  const handleBatchEnable = () => {
    const eligibleTemplates = selectedTemplates.filter(t => 
      t.status === 'Disabled' || t.status === 'Draft'
    );

    if (eligibleTemplates.length === 0) {
      message.warning(t('planTemplate.noBatchEnableTemplates'));
      return;
    }

    const ineligibleCount = selectedTemplates.length - eligibleTemplates.length;
    const confirmContent = (
      <div>
        <p>{t('planTemplate.confirmBatchEnable', { count: eligibleTemplates.length })}</p>
        {ineligibleCount > 0 && (
          <Alert
            message={t('planTemplate.templatesAlreadyEnabled', { count: ineligibleCount })}
            type="info"
            showIcon
            style={{ marginTop: '8px' }}
          />
        )}
      </div>
    );

    confirm({
      title: t('planTemplate.batchEnableConfirm'),
      content: confirmContent,
      icon: <CheckCircleOutlined />,
      okText: t('planTemplate.confirmEnable'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          // 调用批量更新状态API
          const eligibleIds = eligibleTemplates.map(t => t.id);
          const response = await PlanogramTemplateService.batchUpdateStatus(eligibleIds, 'Published');
          
          if (response.success) {
            message.success(t('planTemplate.batchEnableSuccessSimple'));
            await loadTemplates();
          } else {
            message.error(response.errorMessage || ERROR_MESSAGES.NETWORK_ERROR);
          }
        } catch (error) {
          console.error('Batch enable failed:', error);
          message.error(ERROR_MESSAGES.NETWORK_ERROR);
        }
      }
    });
  };

  // 批量禁用模板
  const handleBatchDisable = () => {
    const eligibleTemplates = selectedTemplates.filter(t => 
      t.status === 'Published'
    );

    if (eligibleTemplates.length === 0) {
      message.warning(t('planTemplate.noBatchDisableTemplates'));
      return;
    }

    const templatesWithActivePlans = eligibleTemplates.filter(t => 
      t.usageStatistics?.activePlans > 0
    );

    const confirmContent = (
      <div>
        <p>{t('planTemplate.confirmBatchDisable', { count: eligibleTemplates.length })}</p>
        <Alert
          message={t('planTemplate.disableNotice')}
          type="info"
          showIcon
          style={{ marginTop: '8px' }}
        />
        {templatesWithActivePlans.length > 0 && (
          <Alert
            message={t('planTemplate.templatesWithActivePlansNotice', { count: templatesWithActivePlans.length })}
            type="warning"
            showIcon
            style={{ marginTop: '8px' }}
          />
        )}
      </div>
    );

    confirm({
      title: t('planTemplate.batchDisableConfirm'),
      content: confirmContent,
      icon: <PoweroffOutlined />,
      okText: t('planTemplate.confirmDisable'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          // 调用批量更新状态API
          const eligibleIds = eligibleTemplates.map(t => t.id);
          const response = await PlanogramTemplateService.batchUpdateStatus(eligibleIds, 'Disabled');
          
          if (response.success) {
            message.success(t('planTemplate.batchDisableSuccess'));
            await loadTemplates();
          } else {
            message.error(response.errorMessage || ERROR_MESSAGES.NETWORK_ERROR);
          }
        } catch (error) {
          console.error('Batch disable failed:', error);
          message.error(ERROR_MESSAGES.NETWORK_ERROR);
        }
      }
    });
  };

  // 批量删除模板
  const handleBatchDelete = () => {
    const templatesInUse = selectedTemplates.filter(t => t.status === 'Published');
    
    if (templatesInUse.length > 0) {
      message.warning(t('planTemplate.batchOperationWarning'));
      return;
    }

    const confirmContent = (
      <div>
        <Alert
          message={t('planTemplate.deleteWarningTitle')}
          description={t('planTemplate.deleteWarningDescription')}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <p>{t('planTemplate.batchDeleteConfirmMessage', { count: selectedTemplates.length })}</p>
        <div style={{ marginTop: '12px' }}>
          <Typography.Text strong>{t('planTemplate.templatesWillBeDeleted')}</Typography.Text>
          <ul style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto' }}>
            {selectedTemplates.map(template => (
              <li key={template.id}>
                <Typography.Text code>{template.code}</Typography.Text> - {template.name}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );

    confirm({
      title: t('planTemplate.batchDeleteConfirm'),
      content: confirmContent,
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      okText: t('planTemplate.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      width: 600,
      onOk: async () => {
        try {
          let successCount = 0;
          let failureCount = 0;

          for (const template of selectedTemplates) {
            try {
              const response = await PlanogramTemplateService.deleteTemplate(template.id);
              if (response.success) {
                successCount++;
              } else {
                failureCount++;
              }
            } catch (error) {
              failureCount++;
            }
          }

          const finalMessage = t('planTemplate.batchDeleteResult', { success: successCount, failed: failureCount });
          
          if (successCount > 0) {
            message.success(finalMessage);
            await loadTemplates();
            setSelectedRowKeys([]);
          } else {
            message.error(t('planTemplate.batchDeleteFailed'));
          }
        } catch (error) {
          console.error('Batch delete failed:', error);
          message.error(ERROR_MESSAGES.NETWORK_ERROR);
        }
      }
    });
  };

  const handleDelete = (template: PlanogramTemplate) => {
    confirm({
      title: t('planTemplate.confirmDelete'),
      content: t('planTemplate.confirmDeleteMessage', { name: template.name }),
      okText: t('common.confirm'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await PlanogramTemplateService.deleteTemplate(template.id);
          
          if (response.success) {
            message.success(SUCCESS_MESSAGES.TEMPLATE_DELETED);
            loadTemplates();
          } else {
            message.error(response.message || ERROR_MESSAGES.OPERATION_FAILED);
          }
        } catch (error) {
          console.error('Failed to delete template:', error);
          message.error(ERROR_MESSAGES.NETWORK_ERROR);
        }
      }
    });
  };

  const handleStatusToggle = async (template: PlanogramTemplate) => {
    let newStatus: TemplateStatus;
    let actionText: string;
    
    // 根据当前状态确定目标状态
    if (template.status === 'Draft') {
      newStatus = 'Published';
      actionText = t('planTemplate.publish');
    } else if (template.status === 'Published') {
      newStatus = 'Disabled';
      actionText = t('planTemplate.disable');
    } else if (template.status === 'Disabled') {
      newStatus = 'Published';
      actionText = t('planTemplate.enable');
    } else {
      message.warning(t('planTemplate.cannotChangeStatus'));
      return;
    }

    Modal.confirm({
      title: t('planTemplate.confirmToggleStatus', { action: actionText }),
      content: t('planTemplate.confirmToggleMessage', { action: actionText, name: template.name }),
      okText: t('planTemplate.confirmToggleStatus', { action: actionText }),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await PlanogramTemplateService.batchUpdateStatus([template.id], newStatus);
          
          if (response.success) {
             message.success(t('planTemplate.toggleSuccess', { action: actionText }));
             await loadTemplates();
           } else {
            message.error(response.errorMessage || t('planTemplate.toggleFailed', { action: actionText }));
          }
        } catch (error) {
          console.error('Status toggle error:', error);
          message.error(t('planTemplate.toggleError', { action: actionText }));
        }
      }
    });
  };

  // Get action menu items for each row
  const getActionMenuItems = (template: PlanogramTemplate): MenuProps['items'] => [
    {
      key: 'toggle-status',
      icon: template.status === 'Published' ? <PoweroffOutlined /> : <CheckCircleOutlined />,
      label: template.status === 'Published' ? t('planTemplate.disable') : 
             template.status === 'Draft' ? t('planTemplate.publish') : t('planTemplate.enable'),
      disabled: false, // 状态切换逻辑在handleStatusToggle中处理
      onClick: () => handleStatusToggle(template)
    },
    {
      type: 'divider'
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('planTemplate.delete'),
      danger: true,
      disabled: template.status === 'Published',
      onClick: () => handleDelete(template)
    }
  ];

  // Table columns configuration
  const columns: ColumnsType<PlanogramTemplate> = [
    {
      title: t('planTemplate.templateCodeTable'),
      dataIndex: 'code',
      key: 'code',
      width: 150,
      fixed: 'left',
      render: (code: string) => (
        <Typography.Text code strong>
          {code}
        </Typography.Text>
      )
    },
    {
      title: t('planTemplate.templateName'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: {
        showTitle: false
      },
      render: (name: string, record: PlanogramTemplate) => (
        <Tooltip title={name}>
          <Button
            type="link"
            onClick={() => handleView(record)}
            style={{ padding: 0, height: 'auto' }}
          >
            {name}
          </Button>
        </Tooltip>
      )
    },
    {
      title: t('planTemplate.planTypeColumn'),
      dataIndex: 'planType',
      key: 'planType',
      width: 120,
      render: (planType: string) => {
        const planTypeKey = {
          'Major Plans': 'majorPlans',
          'Regular Plans': 'regularPlans',
          'Minor Plans': 'minorPlans',
          'Urgent Plans': 'urgentPlans',
          'Special Plans': 'specialPlans'
        }[planType];
        return (
          <Tag color="blue">
            {planTypeKey ? t(`planTemplate.planType.${planTypeKey}`) : planType}
          </Tag>
        );
      }
    },
    {
      title: t('planTemplate.templateOwner'),
      dataIndex: 'owner',
      key: 'owner',
      width: 120,
      render: (owner: string, record: PlanogramTemplate) => (
        <Typography.Text style={{ fontSize: '12px' }}>
          {owner || record.owner || '-'}
        </Typography.Text>
      )
    },
    {
      title: t('planTemplate.statusColumn'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: TemplateStatus) => {
        const statusKey = {
          'Published': 'published',
          'Draft': 'draft',
          'Disabled': 'disabled'
        }[status];
        const statusConfig = TEMPLATE_STATUSES.find(s => s.value === status);
        return (
          <Tag color={statusConfig?.color}>
            {statusKey ? t(`planTemplate.status.${statusKey}`) : status}
          </Tag>
        );
      }
    },
    {
      title: t('planTemplate.nextGenerationTime'),
      dataIndex: 'executionCycle.nextGenerationTime',
      key: 'executionCycle.nextGenerationTime',
      width: 160,
      render: (_, record, timestamp: number) => (
        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            {formatDateTime(record.executionCycle?.nextGenerationTime)}
          </Typography.Text>
      )
    },
    {
      title: t('planTemplate.usageStatistics'),
      key: 'usage',
      width: 120,
      render: (_, record: PlanogramTemplate) => (
        <Space direction="vertical" size={0}>
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            {t('planTemplate.usageCount')}: {record.usageStatistics?.usageCount}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            {t('planTemplate.activePlans')}: {record.usageStatistics?.activePlans}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: t('planTemplate.createInfo'),
      key: 'createInfo',
      width: 150,
      render: (_, record: PlanogramTemplate) => (
        <Space direction="vertical" size={0}>
          <Typography.Text style={{ fontSize: '12px' }}>
            {record.creator}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(record.createTime).format('YYYY-MM-DD HH:mm')}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: t('planTemplate.lastUpdate'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 120,
      render: (gmtModified: string) => (
        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
          {dayjs(gmtModified).format('YYYY-MM-DD HH:mm')}
        </Typography.Text>
      )
    },
    {
      title: t('planTemplate.actions'),
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record: PlanogramTemplate) => (
        <Space size="small">
          <Tooltip title={t('planTemplate.viewDetail')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title={t('planTemplate.edit')}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              disabled={record.status === 'Published'}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: getActionMenuItems(record) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
            />
          </Dropdown>
        </Space>
      )
    }
  ];

  const formatDateTime = (timestamp: number): string => {
    return timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '';
  };

  // Handle search with form values
  const handleSearch = (values: any) => {    
    const newFilters: TemplateFilterParams = {
      searchText: values.searchText,
      planType: values.planType,
      status: values.status,
      creator: values.creator,
      nextGenerationTimeStart: values.nextGenerationTime?.[0] ? values.nextGenerationTime[0].startOf('day').valueOf() : undefined,
      nextGenerationTimeEnd: values.nextGenerationTime?.[1] ? values.nextGenerationTime[1].endOf('day').valueOf() : undefined
    };
    setFilters(newFilters);
    const newPagination = { ...pagination, current: 1 };
    setPagination(newPagination);
    loadTemplates(newFilters, newPagination);
  };

  // Handle filter reset
  const handleFilterReset = () => {
    form.resetFields();
    const emptyFilters: TemplateFilterParams = {};
    setFilters(emptyFilters);
    const newPagination = { ...pagination, current: 1 };
    setPagination(newPagination);
    loadTemplates(emptyFilters, newPagination);
    return emptyFilters; // Return empty filters for FilterPanel to reset form values
  };

  // Get selected templates
  const selectedTemplates = templates.filter(template => 
    selectedRowKeys.includes(template.id)
  );

  return (
    <div>
      {/* Page Header removed as requested */}

     {/* Filter Panel Section */}
    <FilterPanel
        form={form}
        onFilter={handleSearch}
        onReset={handleFilterReset}
        collapsible={true}
        defaultCollapsed={false}
        title={t('menu.template')}
        className=""
      >
        <Col span={6}>
          <Form.Item name="searchText" label={t('planTemplate.filter.keyword')}>
            <Input placeholder={t('planTemplate.searchPlaceholder')} allowClear />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="planType" label={t('planTemplate.filter.planType')}>
            <Select placeholder={t('planTemplate.selectPlanType')} allowClear>
              {PLAN_TYPES.map(type => {
                const planTypeKey = {
                  'Major Plans': 'majorPlans',
                  'Regular Plans': 'regularPlans',
                  'Minor Plans': 'minorPlans',
                  'Urgent Plans': 'urgentPlans',
                  'Special Plans': 'specialPlans'
                }[type.value];
                return (
                  <Select.Option key={type.value} value={type.value}>
                    {planTypeKey ? t(`planTemplate.planType.${planTypeKey}`) : type.label}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="status" label={t('planTemplate.filter.status')}>
            <Select placeholder={t('planTemplate.selectStatus')} allowClear>
              {TEMPLATE_STATUSES.map(status => {
                const statusKey = {
                  'Published': 'published',
                  'Draft': 'draft',
                  'Disabled': 'disabled'
                }[status.value];
                return (
                  <Select.Option key={status.value} value={status.value}>
                    {statusKey ? t(`planTemplate.status.${statusKey}`) : status.label}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="creator" label={t('planTemplate.filter.creator')}>
            <Input placeholder={t('planTemplate.inputCreator')} allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="nextGenerationTime" label={t('planTemplate.filter.nextGenerationTime')}>
            <RangePicker 
              style={{ width: '100%' }} 
              placeholder={[t('common.selectStartTimePlaceHolder'), t('common.selectEndTimePlaceHolder')]}
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* Main Content Area */}
      <div style={{ 
        flex: 1, 
        overflow: 'auto', 
      }}>
      

        {/* Template List Card */}
        <Card style={{ 
          borderRadius: '16px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <BatchActionToolbar
              selectedCount={selectedRowKeys.length}
              actions={[
                {
                  key: 'enable',
                  label: t('planTemplate.batchEnable'),
                  icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                  onClick: handleBatchEnable,
                  disabled: !selectedTemplates.some(t => t.status === 'Disabled' || t.status === 'Draft')
                },
                {
                  key: 'disable',
                  label: t('planTemplate.batchDisable'),
                  icon: <StopOutlined style={{ color: '#faad14' }} />,
                  onClick: handleBatchDisable,
                  disabled: !selectedTemplates.some(t => t.status === 'Published')
                },
                {
                  key: 'delete',
                  label: t('planTemplate.batchDelete'),
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: handleBatchDelete,
                  disabled: selectedTemplates.some(t => t.status === 'Published')
                }
              ]}
              onClearSelection={() => setSelectedRowKeys([])}
            />
            <Space size="middle">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenWizard?.('create')}
                style={{ 
                  borderRadius: '8px',
                  backgroundColor: '#3b82f6',
                  borderColor: '#3b82f6',
                  boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
                }}
              >
                {t('planTemplate.create')}
              </Button>
            </Space>
          </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          rowSelection={rowSelection}
          loading={loading}
          scroll={{ x: 1200, y: 500 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('common.paginationTotal', { start: range[0], end: range[1], total }),
            pageSizeOptions: UI_CONFIG.TABLE_PAGE_SIZE_OPTIONS,
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange
          }}
        />
      </Card>
    </div>
      <TemplateWizard
          visible={wizardState.visible}
          mode={wizardState.mode}
          templateId={wizardState.templateId}
          onComplete={handleWizardComplete}
          onCancel={handleCloseWizard}
      />
    </div>
  );
};

export default PlanogramTemplateList;